<template>
	<app-title-bar
		:icon="['fas', 'fa-clipboard-check']"
		subtitle="Hire Desk"
		title="Pick / Load Item Detail"
		:badges="[contract?.contract_number, contract?.account_name]">
		<div class="flex gap-3">
			<app-button to="/hire-desk/pick-items" icon="fa-circle-left"> Back </app-button>

			<resource-details :loading="loading || !contract" :resource="contract" />

			<status-badge
				v-if="
					contract?.status && contract.status.length > 0 && contract.status[0].load_status
				"
				:status="contract.status[0]"
				status-key="load_status" />

			<status-badge
				v-else-if="contract?.status && contract.status.length > 0"
				:status="contract.status[0]"
				status-key="pick_status" />
		</div>
	</app-title-bar>

	<div class="flex flex-col-reverse gap-4">
		<app-panel class="grow" title="Item Details">
			<template #header>
				<div class="flex items-center gap-3">
					<app-badge
						v-for="(weight, depot) in weightByDepot"
						:key="depot"
						theme="info"
						size="small"
						class="gap-1 py-2">
						<font-awesome-icon icon="fa-solid fa-weight-hanging" class="w-4" />
						<b>{{ depot }}:</b> {{ Math.round(weight * 100) / 100 }}
					</app-badge>

					<contract-notes v-if="contract?.status[0].notes">
						{{ contract?.status[0].notes }}
					</contract-notes>

					<contract-notes
						v-if="contract?.status[0].item_notes"
						title="Ancillary Items"
						button="Ancillaries">
						{{ contract?.status[0].item_notes }}
					</contract-notes>
				</div>
			</template>

			<item-details-table
				:items="items"
				:loading="loading"
				@updated-items="updated"
				@selected-items="(i: ContractItem[]) => (selected = i)" />

			<div class="flex flex-col lg:flex-row lg:items-center lg:justify-end mt-4 gap-3">
				<app-dropdown
					v-model="delType"
					size="large"
					:options="delTypeOptions"
					:disabled="loading || selected.length == 0"
					@selected="getVehicles()">
					Delivery Type
				</app-dropdown>

				<div class="flex">
					<depots-dropdown
						v-model="vehicleDepot"
						size="large"
						input-group="left"
						class="w-full lg:w-[100px]"
						:disabled="loading || !delType"
						@update:model-value="getVehicles" />

					<app-loader v-if="vehiclesLoading" />
					<app-dropdown
						v-if="!vehiclesLoading"
						v-model="vehicle"
						class="w-full lg:w-[300px]"
						size="large"
						:label-key="['name', 'code']"
						value-key="id"
						group-by="depot"
						input-group="right"
						:options="vehicleOptions"
						:disabled="loading || vehicleOptions.length == 0">
						Vehicle
					</app-dropdown>
				</div>

				<app-button :disabled="!canSendToLoad" @click="send"> Send to Load </app-button>
				<app-button :disabled="!changed" @click="save"> Save </app-button>
			</div>
		</app-panel>

		<confirm-dialog
			:open="depotWarning"
			@confirmed="send(true)"
			@canceled="depotWarning = false">
			Warning: Items have been selected from more than one depot. Proceed anyway?
		</confirm-dialog>
	</div>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted } from 'vue'
	import ResourceDetails from '@/components/partials/panels/ResourceDetails.vue'
	import ItemDetailsTable from '@/components/datatables/hire-desk/pick/ItemDetailsTable.vue'
	import StatusBadge from '@/components/partials/cat2/StatusBadge.vue'
	import { ContractItem } from '@/data/schema/contract-item'
	import useContracts from '@/composables/contracts'
	import useVehicles from '@/composables/depot'
	import { useRoute } from 'vue-router'
	import type { Contract } from '@/data/schema/contract'
	import useToaster from '@/stores/toaster'
	import useAuthStore from '@/stores/auth'
	import ContractService from '@/services/contract'
	import type { StatusUpdate } from '@/data/interfaces/status-update'
	import { splitWeightByDepot } from '@/utils/contract-items'
	import DepotsDropdown from '@/components/partials/dropdowns/DepotsDropdown.vue'

	// Resources
	const route = useRoute()
	const api = new ContractService()
	const toaster = useToaster()
	const authStore = useAuthStore()

	// Data
	const contractNumber = ref()
	const pickRefs = ref([])
	const deliveryMethod = ref()
	const loading = ref(true)
	const contract = ref<Contract>()
	const items = ref<Array<ContractItem>>([])
	const weightByDepot = ref()
	const selected = ref<ContractItem[]>([])
	const vehicleDepot = ref()
	const depotWarning = ref(false)

	const {
		delType,
		delTypeOptions,
		vehicle,
		vehicleOptions,
		vehiclesLoading,
		selectedVehicle,
		getVehicles,
		resetLoad
	} = useVehicles(
		() => contract.value,
		() => vehicleDepot.value
	)

	const { sendToLoad } = useContracts()

	const canSendToLoad = computed(() => {
		return (
			delType.value &&
			selected.value.length > 0 &&
			(selectedVehicle.value || delType.value == '2')
		)
	})

	// If user uses select-all, multiple depots are possible
	// This will return the unique depot count
	const uniqueDepots = computed(() => {
		const depots = selected.value
			.map((item) => item.status_depot)
			.filter((i) => i !== undefined)
		const uniqueDepots = new Set(depots)
		return uniqueDepots.size
	})

	/**
	 * Get contract and contract item data
	 */
	async function getData() {
		loading.value = true

		const { fetchData } = useContracts()

		try {
			const data = await fetchData(contractNumber.value, {
				withContractStatus: true,
				withPickStatus: true,
				pickRefs: pickRefs.value,
				deliveryMethod: deliveryMethod.value
			})
			contract.value = data.contract
			items.value = data.items

			items.value = items.value.map((item) => ({
				...item,
				stock_status: item.pick_status?.[0]?.status ?? null,
				status_depot: item.pick_status?.[0]?.depot ?? item.depot,
				changed: false
			}))

			weightByDepot.value = splitWeightByDepot(items.value, 'pick')

			loading.value = false
		} catch (error) {
			console.error(error)
			loading.value = false
		}
	}

	/**
	 * Items have been updated
	 */
	function updated(item: ContractItem) {
		const index = items.value.findIndex((i) => i.id === item.id)

		if (index !== -1) {
			items.value[index].changed = !items.value[index].changed
		}
	}

	const changed = computed(() => {
		return items.value.some((item) => item.changed)
	})

	function save() {
		const changedItems = items.value.filter((item) => item.changed === true)

		const saveItems = changedItems.map((item) => ({
			id: item.id,
			status_id: item.pick_status?.[0].id,
			tos: item.pick_status?.[0].tos,
			hire_desk_complete: item.pick_status?.[0].hire_desk_complete
		}))

		toaster.working({
			text: 'Saving contract item changes...',
			dismissable: false,
			timeout: 0
		})

		api.bulkUpdateStatus(saveItems as StatusUpdate[], 'pick-status')
			.then(() => {
				toaster.clear()
				toaster.success({ text: 'Contract saved successfully!' })
				getData()
			})
			.catch((error: any) => {
				toaster.clear()
				toaster.error({ text: 'Error occurred updating contract' })
				console.error(error)
			})
	}

	/**
	 * Call Contracts:SendToLoad composable function
	 */
	function send(confirmed = false) {
		// Intercept with warning if more than 1 depot
		if (uniqueDepots.value > 1 && !confirmed) {
			depotWarning.value = true
			return false
		}

		sendToLoad(selected.value, delType.value, selectedVehicle.value!).then(() => {
			getData()
			resetLoad()
		})
	}

	onMounted(() => {
		contractNumber.value = route.params.id
		vehicleDepot.value = authStore.user!.current_depot

		if (contractNumber.value) {
			getData()
		}
	})
</script>
